/**
 * Team Grid easter egg — "/launch" + Enter reveals a hidden team member.
 *
 * Scoped to this block: the x-data lives on the grid's <ul>, so the phrase is
 * only listened for on pages that actually render a Team Grid. The listener
 * itself is bound to the window (see @keydown.window in index.twig) so the grid
 * never needs focus.
 *
 * Deliberately not persisted. Every page load starts locked again — that is the
 * whole point of an easter egg, and it keeps the headshot out of the DOM (and
 * off the network) for anyone who does not go looking.
 */
window.TeamGridEasterEgg = (phrase = '/launch') => ({
	phrase: String(phrase).toLowerCase(),
	typed: '',
	unlocked: false,

	/** True while the user is typing into a field, where the phrase must not fire. */
	isTypingTarget(el) {
		return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
	},

	onKey(event) {
		if (this.unlocked || this.isTypingTarget(event.target)) return;

		const key = event.key || '';

		if (key === 'Enter') {
			// endsWith, not equality: the buffer is a rolling window of recent
			// keystrokes, so whatever came before the phrase is ignored. That is
			// what lets a typo be fixed with Backspace and retyped — dropping the
			// buffer the moment it diverged would have thrown away the good
			// prefix too, leaving Backspace with nothing to undo.
			if (this.typed.endsWith(this.phrase)) this.reveal();
			this.typed = '';
			return;
		}

		if (key === 'Backspace') {
			this.typed = this.typed.slice(0, -1);
			return;
		}

		// Ignore modifiers, arrows, F-keys — anything that isn't a character.
		if (key.length !== 1) return;

		// Keep twice the phrase length: enough for Backspace to walk back through
		// a mistake, bounded so idle typing can't grow it without limit.
		this.typed = (this.typed + key.toLowerCase()).slice(-this.phrase.length * 2);
	},

	reveal() {
		// Only flips the flag. Everything that needs the card in the DOM lives in
		// land(), which x-init calls on the card itself.
		//
		// It used to happen here behind $nextTick, and never ran: x-if inserts its
		// clone inside Alpine's batched mutateDom(), so $nextTick can fire before
		// el.after(clone) has run — getElementById returned null and the whole
		// sequence bailed. x-if also puts the clone in its own scope, so the
		// $refs.disco fallback was undefined for the same reason.
		this.unlocked = true;
	},

	/**
	 * Called by x-init on the card, so the element is guaranteed to exist and be
	 * attached. rAF waits one frame so the browser has laid it out before we
	 * measure a scroll target.
	 */
	land(card) {
		requestAnimationFrame(() => {
			const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

			// Fade in. A class added in the same frame the element was inserted is
			// treated as its initial style and never animates, so commit opacity-0
			// with a forced reflow first. Reduced motion skips it via
			// motion-reduce:transition-none in the markup.
			void card.offsetWidth;
			card.classList.replace('opacity-0', 'opacity-100');

			// Anchor the page to him, without a history entry and without the
			// instant jump that setting location.hash would cause.
			if (window.history?.replaceState) {
				window.history.replaceState(null, '', '#disco-chicken');
			}

			card.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });

			// Focus waits for the scroll to finish. Calling focus() straight after
			// scrollIntoView cuts the animation short in some browsers, and Safari
			// ignores preventScroll outright.
			this.focusWhenSettled(card, reduced);
		});
	},

	/**
	 * Move focus once scrolling has stopped.
	 *
	 * scrollend is Chrome/Firefox only, so a timer backs it up — and doubles as
	 * the path for the case where no scroll happens at all (card already in
	 * view), where scrollend never fires.
	 */
	focusWhenSettled(card, reduced) {
		if (reduced) {
			card.focus({ preventScroll: true });
			return;
		}

		let done = false;
		const settle = () => {
			if (done) return;
			done = true;
			card.focus({ preventScroll: true });
		};

		if ('onscrollend' in window) {
			window.addEventListener('scrollend', settle, { once: true });
		}
		setTimeout(settle, 800);
	},
});
